/* eslint-disable no-redeclare */
import { z } from 'zod';

/**
 * Runtime contract for a usable QuinStreet listing. Deliberately lenient,
 * mirroring Nova's fetcher (MP-3842): a listing is dropped only when it has no
 * `clickurl` (no destination, no revenue) or no `logo` (our TYP cards always
 * render it); every other field falls back to a safe default because
 * `cpc`/`vendorKey` are tracking-only.
 */
export const QuinStreetListingDto = z.object({
  clickurl: z.string().min(1),
  title: z.string().catch(''),
  // Rich HTML: a selling-point list, sometimes with QuinStreet's own pixel.
  description: z.string().catch(''),
  logo: z.string().min(1),
  displayname: z.string().catch(''),
  sitehost: z.string().catch(''),
  // The live API sends numbers as numeric strings or numbers interchangeably.
  rank: z.coerce.number().catch(Number.MAX_SAFE_INTEGER),
  vendorKey: z.coerce.number().int().catch(0),
  cpc: z.coerce.number().catch(0),
  imp_pixel: z.string().optional().catch(undefined),
});

/**
 * A listing that passed {@link QuinStreetListingDto}.
 */
export type QuinStreetListingDto = z.infer<typeof QuinStreetListingDto>;

/**
 * Envelope of the listings API response. Listings stay `unknown` so one
 * malformed listing does not discard the rest.
 */
export const QuinStreetResponseDto = z.object({
  response: z.object({
    listingset: z.object({
      searchid: z.string().optional(),
      listing: z.array(z.unknown()).optional(),
    }),
  }),
});

/**
 * Parsed listings API response envelope.
 */
export type QuinStreetResponseDto = z.infer<typeof QuinStreetResponseDto>;

/**
 * QuinStreet `individuals[]` entry. Every field is PII.
 */
export type QuinStreetIndividual = {
  relation_to_applicant: 'Self';
  first_name?: string;
  last_name?: string;
  gender?: 'M' | 'F';
  birthdate?: string;
  age?: number;
  tobacco_use?: 0 | 1;
};

/**
 * Datapass JSON body POSTed to the QuinStreet listings API.
 * `contact` and `individuals` carry lead PII — never log this object.
 */
export type QuinStreetRequestBody = {
  tracking: {
    content_type: 'json';
    ni_ad_client: string;
    inventory_type: 'thankyoupage';
    coverage_type: string;
    ni_zc?: string;
    state?: string;
    ip?: string;
    ua?: string;
    ni_ref?: string;
    ni_lp_url?: string;
    ni_session_ref?: string;
    ni_trn_id: string;
    ni_var1?: string;
    ni_var2?: string;
    ni_var3?: string;
  };
  contact: {
    first_name?: string;
    last_name?: string;
    email?: string;
    home_phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  individuals: QuinStreetIndividual[];
};
